import React, { useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import styled from "styled-components";
import { Drawer } from "components/nav/drawer/drawer.component";
import { NavBar } from "components/nav/navBar/navBar.component";
import SimflexityLogo from "images/icons/svg/v2/simflexity-logo.svg";
import {
  PrivateRoute,
  PublicRoute,
} from "web-apps/company/components/customRoute/customRoute.component";
import { AnalyticsPage } from "./pages/analytics/analytics.page";
import { ApplicantDetailPage } from "./pages/application/applicantDetail.page";
import { ApplicationPage } from "./pages/application/application.page";
import { GigApplicantsPage } from "./pages/application/gigApplicants.page";
import { ChatPage } from "./pages/chat/chat.page";
import { ClockStampPage } from "./pages/clockStamp/clockStamp.page";
import { CompanySelectPage } from "./pages/companySelect/companySelect.page";
import { CompanySettingsV2Page } from "./pages/companySettingsV2/companySettingsV2.page";
import { DesignPage } from "./pages/design/design.page";
import { LoginPage } from "./pages/login/login.page";
import { FavoriteDetailPage } from "./pages/profile/favorite/favoriteDetail.page";
import { FavoritePage } from "./pages/profile/favorite/favorite.page";
import { RegistrationPage } from "./pages/registration/registration.page";
import { SalaryPage } from "./pages/salary/salary.page";
import { SchedulePage } from "./pages/schedule/schedule.page";
import { SignUpPage } from "./pages/signUp/signUp.page";
import { StaffPage } from "./pages/staff/staff.page";
import { TipsPage } from "./pages/tips/tips.page";
import { TimeReportsV2Page } from "./pages/timereports/timeReportsV2.page";
import { ApplicationStoreProvider } from "./stores/applicationStore/applicationStore";
import { ChatStoreProvider } from "./stores/chatStore/chatStore";
import {
  CompanyStoreProvider,
  useCompanyStore,
} from "./stores/companyStore/companyStore";
import { MonthSalaryStoreProvider } from "./stores/monthSalaryStore/monthSalaryStore";
import { OrganizationalStructureStoreProvider } from "./stores/organizationalStructureStore/organizationalStructureStore";
import { ScheduleStoreProvider } from "./stores/schedule/scheduleStore";
import { StaffingWorkersStoreProvider } from "./stores/staffingStore/staffingWorkersStore";
import { TimeReportStoreProvider } from "./stores/timeReportStore/timeReportStore";
import { TimeReportsV2StoreProvider } from "./stores/timeReportsV2/timeReportsV2Store";
import { TipStoreProvider } from "./stores/tipStore/tipStore";
import {
  isFreeSubscription,
  isSubscriptionLoadedForCompany,
} from "./utils/subscription.logic";

const AppShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  position: relative;
`;

const AuthLogo = styled.img`
  position: absolute;
  top: 28px;
  left: 28px;
  width: 148px;
  height: auto;
  object-fit: contain;
  z-index: 2;
`;

const FreeRestrictedRoute: React.FC = ({
  children,
}): React.ReactElement | null => {
  const [companyState] = useCompanyStore();

  if (companyState.subscription.isLoading || companyState.company == null)
    return null;
  if (
    !isSubscriptionLoadedForCompany({
      companyId: companyState.company.id,
      isLoading: companyState.subscription.isLoading,
      subscription: companyState.subscription.info,
    }) ||
    isFreeSubscription(companyState.subscription.info)
  ) {
    return <Redirect to="/company/schedule" />;
  }

  return <>{children}</>;
};

export const CompanyApp = (): JSX.Element => {
  const { path } = useRouteMatch();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  const isProd = hostname === "app.simflexity.com";
  const shouldHideNavigation =
    location.pathname === `${path}/login` ||
    location.pathname === `${path}/sign-up` ||
    location.pathname === `${path}/registration`;

  return (
    <CompanyStoreProvider>
      <ChatStoreProvider>
        <ApplicationStoreProvider>
          <TimeReportStoreProvider>
            <TimeReportsV2StoreProvider>
              <MonthSalaryStoreProvider>
                <OrganizationalStructureStoreProvider>
                  <TipStoreProvider>
                    <StaffingWorkersStoreProvider>
                      <ScheduleStoreProvider>
                        {!shouldHideNavigation && (
                          <Drawer open={open} setOpen={setOpen} />
                        )}
                        <AppShell>
                          {shouldHideNavigation && (
                            <AuthLogo
                              src={SimflexityLogo}
                              alt="Simflexity logo"
                            />
                          )}
                          {!shouldHideNavigation && (
                            <NavBar open={open} setOpen={setOpen} />
                          )}
                          <Switch>
                            <Route
                              exact
                              path={path}
                              render={() => <Redirect to={`${path}/login`} />}
                            />
                            <PublicRoute path={`${path}/login`}>
                              <LoginPage />
                            </PublicRoute>
                            <PublicRoute path={`${path}/sign-up`}>
                              <SignUpPage />
                            </PublicRoute>
                            <PrivateRoute path={`${path}/company-select`}>
                              <CompanySelectPage />
                            </PrivateRoute>
                            <PublicRoute path={`${path}/registration`}>
                              <RegistrationPage />
                            </PublicRoute>
                            <PrivateRoute path={`${path}/overview`}>
                              <div>Overview</div>
                            </PrivateRoute>
                            {!isProd && (
                              <PrivateRoute path={`${path}/design`}>
                                <DesignPage />
                              </PrivateRoute>
                            )}
                            <PrivateRoute
                              path={`${path}/applications/application/:id`}
                              render={(props) => (
                                <FreeRestrictedRoute>
                                  <ApplicantDetailPage {...props} />
                                </FreeRestrictedRoute>
                              )}
                            />
                            <PrivateRoute
                              path={`${path}/applications/:id`}
                              render={(props) => (
                                <FreeRestrictedRoute>
                                  <GigApplicantsPage {...props} />
                                </FreeRestrictedRoute>
                              )}
                            />
                            <PrivateRoute path={`${path}/applications`}>
                              <FreeRestrictedRoute>
                                <ApplicationPage />
                              </FreeRestrictedRoute>
                            </PrivateRoute>
                            <PrivateRoute path={`${path}/stampclock`}>
                              <ClockStampPage />
                            </PrivateRoute>
                            <PrivateRoute
                              path={`${path}/profile/favorites/:id`}
                              render={(props) => (
                                <FavoriteDetailPage {...props} />
                              )}
                            />
                            <PrivateRoute path={`${path}/profile/favorites`}>
                              <FavoritePage />
                            </PrivateRoute>
                            <PrivateRoute path={`${path}/settings`}>
                              <CompanySettingsV2Page />
                            </PrivateRoute>
                            <PrivateRoute path={`${path}/chat`}>
                              <FreeRestrictedRoute>
                                <ChatPage />
                              </FreeRestrictedRoute>
                            </PrivateRoute>
                            <PrivateRoute path={`${path}/schedule`}>
                              <SchedulePage />
                            </PrivateRoute>
                            <PrivateRoute path={`${path}/time-reports`}>
                              <TimeReportsV2Page />
                            </PrivateRoute>
                            <PrivateRoute path={`${path}/tips`}>
                              <FreeRestrictedRoute>
                                <TipsPage />
                              </FreeRestrictedRoute>
                            </PrivateRoute>
                            <PrivateRoute path={`${path}/staff`}>
                              <StaffPage />
                            </PrivateRoute>
                            <PrivateRoute path={`${path}/salary`}>
                              <SalaryPage />
                            </PrivateRoute>
                            <PrivateRoute path={`${path}/analytics`}>
                              <FreeRestrictedRoute>
                                <AnalyticsPage />
                              </FreeRestrictedRoute>
                            </PrivateRoute>
                          </Switch>
                        </AppShell>
                      </ScheduleStoreProvider>
                    </StaffingWorkersStoreProvider>
                  </TipStoreProvider>
                </OrganizationalStructureStoreProvider>
              </MonthSalaryStoreProvider>
            </TimeReportsV2StoreProvider>
          </TimeReportStoreProvider>
        </ApplicationStoreProvider>
      </ChatStoreProvider>
    </CompanyStoreProvider>
  );
};
